import React from 'react'
import {isLoqateCountry} from '../../../../utils/address-provider-countries'
import {isInternationalTaxIdCountry} from '../../../../utils/country-registry'
import {filterSelectableCountries} from '../../../../utils/selectable-countries'
import {StyledBusinessNameIcon as BusinessNameIcon} from '../../../shared/icons/icons.styles'
import {StyledClientNameIcon as ClientNameIcon} from '../../../shared/icons/icons.styles'
import {StyledAddressIcon as AddressIcon} from '../../../shared/icons/icons.styles'
import {StyledEmailIcon as EmailIcon} from '../../../shared/icons/icons.styles'
import {StyledLocationIcon as LocationIcon} from '../../../shared/icons/icons.styles'
import {StyledPhoneIcon as PhoneIcon} from '../../../shared/icons/icons.styles'

/* ---------------------------------- */
/* ICONS */
/* ---------------------------------- */

// const IconWrapper = ({children}) => (
//   <span
//     style={{
//       display: 'flex',
//       alignItems: 'center',
//       justifyContent: 'center',
//       width: 18,
//       height: 18,
//       color: '#667085',
//     }}
//   >
//     {children}
//   </span>
// )

// const createIcon = svg => () => <IconWrapper>{svg}</IconWrapper>

// export const BuildingIcon = createIcon(
//   <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
//     <path
//       d="M4 21V5C4 4.44772 4.44772 xxxxxx4 5 4H19C19.5523 4 20 4.44772 20 5V21"
//       stroke="currentColor"
//       strokeWidth="2"
//     />
//     <path d="M9 21V15H15V21" stroke="currentColor" strokeWidth="2" />
//   </svg>,
// )

// export const EmailIcon = createIcon(
//   <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
//     <path d="M4 6H20V18H4V6Z" stroke="currentColor" strokeWidth="2" />
//     <path d="M4 7L12 13L20 7" stroke="currentColor" strokeWidth="2" />
//   </svg>,
// )

// export const UserIcon = createIcon(
//   <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
//     <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
//     <path
//       d="M4 20C4 16.6863 7.58172 14 12 14C16.4183 14 20 16.6863 20 20"
//       stroke="currentColor"
//       strokeWidth="2"
//     />
//   </svg>,
// )

/* ---------------------------------- */
/* ROLE HELPERS */
/* ---------------------------------- */

const normalize = value => value?.toLowerCase().replace(/\s/g, '')

// Roles that get the Keap Partner / Legacy Partner split instead of the single
// "Legacy Partner" option every other role sees. Exported so useAccountTree.js can
// default salesRepOrPartner to 'keap-partner' for these same roles without
// duplicating the list.
export const KEAP_LEGACY_PARTNER_ROLES = ['PartnerOps', 'Admin', 'TSS']

const ROLE_GROUPS = {
  LEAD_ID_ACCESS: ['PremisePilot', 'LeadConverter'],

  KEAP_LEGACY_PARTNER_ACCESS: KEAP_LEGACY_PARTNER_ROLES,
}

// const hasRoleAccess = (roles = [], allowedRoles = []) => {
//   const normalizedRoles = new Set(roles.map(normalize))

//   return allowedRoles.some(role => normalizedRoles.has(normalize(role)))
// }

const hasRoleAccess = (roles = [], allowedRoles = []) => {
  const roleList = Array.isArray(roles) ? roles : [roles]
  const normalizedRoles = new Set(roleList.filter(Boolean).map(normalize))
  return allowedRoles.some(role => normalizedRoles.has(normalize(role)))
}

/* ---------------------------------- */
/* OPTIONS */
/* ---------------------------------- */

// Shown to every role except PartnerOps/Admin/TSS (which get the Keap/Legacy split
// below) and the roles the toggle is hidden for entirely (InfluencerOps, KeapPartner,
// Reseller). The value stays 'partner' (matches PARTNER_VALUES and every other call
// site that checks for it) — only the displayed label reads "Legacy Partner" per spec.
const SALES_REP_OPTIONS_FULL = [
  {value: 'sales-rep', label: 'Sales Rep'},
  {value: 'partner', label: 'Legacy Partner'},
  {value: 'no', label: 'No'},
]

const SALES_REP_OPTIONS_KEAP_LEGACY = [
  {value: 'sales-rep', label: 'Sales Rep'},
  {value: 'keap-partner', label: 'Keap Partner'},
  {value: 'legacy-partner', label: 'Legacy Partner'},
  {value: 'no', label: 'No'},
]

// PartnerOps' entire job is attributing the sale to a partner — no "Sales Rep" (self)
// or "No" choice, unlike Admin/TSS (SALES_REP_OPTIONS_KEAP_LEGACY above) who can also
// act as a normal rep or decline attribution.
const PARTNER_ONLY_ROLES = ['PartnerOps']

const SALES_REP_OPTIONS_KEAP_LEGACY_ONLY = [
  {value: 'keap-partner', label: 'Keap Partner'},
  {value: 'legacy-partner', label: 'Legacy Partner'},
]

const OVERRIDE_SST_OPTIONS = [
  {value: 'yes', label: 'Yes'},
  {value: 'no', label: 'No'},
]

const getSalesRepOptions = roles => {
  if (hasRoleAccess(roles, PARTNER_ONLY_ROLES)) {
    return SALES_REP_OPTIONS_KEAP_LEGACY_ONLY
  }
  return hasRoleAccess(roles, ROLE_GROUPS.KEAP_LEGACY_PARTNER_ACCESS)
    ? SALES_REP_OPTIONS_KEAP_LEGACY
    : SALES_REP_OPTIONS_FULL
}

// Any salesRepOrPartner value that represents "this sale is on behalf of a partner" —
// the original 'partner' value (still used by roles outside KEAP_LEGACY_PARTNER_ACCESS)
// plus the two values that replace it for PartnerOps/Admin/TSS.
export const PARTNER_VALUES = ['partner', 'keap-partner', 'legacy-partner']

// Bill To only applies to a location that has a parent to bill against, so it's
// always hidden on the parent card itself. It used to also be hidden entirely for
// KeapPartner, but Keap Partner reps need the same Parent Pay / Self Pay choice per
// child location as every other role — so it's now shown for KeapPartner children too.
const canShowBillTo = ({isParent}) => !isParent

/* ---------------------------------- */
/* FIELD CONFIG */
/* ---------------------------------- */

// Filtered through isSelectableCountry so VN stays hidden here until its turn
// in the PoC rollout, same as CountrySelector.utils.js's COUNTRIES list.
const COUNTRY_OPTIONS = filterSelectableCountries([
  {value: 'US', label: 'United States'},
  {value: 'AU', label: 'Australia'},
  {value: 'NZ', label: 'New Zealand'},
  {value: 'CA', label: 'Canada'},
  {value: 'BB', label: 'Barbados'},
  {value: 'KY', label: 'Cayman Islands'},
  // PoC — Loqate-backed countries (see src/utils/address-provider-countries.js)
  {value: 'ES', label: 'Spain'},
  {value: 'IT', label: 'Italy'},
  {value: 'IN', label: 'India'},
  {value: 'PH', label: 'Philippines'},
  {value: 'VN', label: 'Vietnam'},
  {value: 'GR', label: 'Greece'},
])

// Per-country field terminology for the PoC rollout countries, per the
// requirements doc's country field tables — "State" only genuinely applies to
// India; the others use "Province" (or a locality/comune variant for city) and
// country-specific postal-code terms (CAP/PIN/ZIP), not generic labels.
const STATE_FIELD_LABEL_BY_COUNTRY = {
  ES: 'Province',
  IT: 'Province',
  PH: 'Province',
  VN: 'Province',
  GR: 'Region',
}

const CITY_FIELD_LABEL_BY_COUNTRY = {
  ES: 'City / Locality',
  IT: 'Comune / Locality',
  IN: 'City / Town / Village',
  PH: 'City / Municipality',
  // Loqate's VN Retrieve populates this slot with the ward/commune (e.g.
  // "Phường Phú Thuận"), not the metro city — matches the doc's "ward/commune"
  // field, not "city" as commonly understood.
  VN: 'Ward / Commune',
}

const POST_CODE_FIELD_LABEL_BY_COUNTRY = {
  IT: 'Postal Code (CAP)',
  IN: 'PIN Code',
  PH: 'ZIP Code',
}

export function buildFieldConfigMap({
  roles,
  account,
  values,
  selectedCountry,
  selectedTransactionType,
  states,
  isAddLocation = false,
  isChildLocation = false,
  // FULSAS-11828: true only when editing an EXISTING child location whose Location Name was
  // explicitly set at creation (RECURLY_ACCOUNT.ACCOUNT_LOCATION_NAME is non-empty). Never true
  // for New Account / New Location (isAddLocation) — those are brand new records with nothing
  // stored yet, so Business Name stays visible for them per the story's "user can fill in both"
  // requirement. Defaults false so every other caller of buildFieldConfigMap is unaffected.
  hasStoredLocationName = false,
  // FULSAS-11921: driven by the Enable_Internal_Account_Checkbox DB feature flag (entity_type
  // Role, entity_value Admin). Defaults false so callers that don't pass it hide the field.
  enableInternalAccountCheckbox = false,
}) {
  const isParent = account?.type === 'parent'

  return {
    businessName: {
      labelLeftIcon: <BusinessNameIcon />,
      // Hidden only on Edit of a child location that already has a Location Name — Company
      // Name is already sourced from Location Name in that case, so Business Name is redundant.
      visible: !(isChildLocation && hasStoredLocationName),
    },

    email: {
      labelLeftIcon: <EmailIcon />,
      disabled: isParent && !isAddLocation,
    },
    // -----------------------------------------
    // these are related to proposal tool
    clientEmail: {
      labelLeftIcon: <EmailIcon />,
    },
    clientPhone: {
      labelLeftIcon: <PhoneIcon />,
    },
    clientName: {
      labelLeftIcon: <ClientNameIcon />,
    },
    //------------------------------------------

    firstName: {
      labelLeftIcon: <ClientNameIcon />,
    },

    lastName: {
      labelLeftIcon: <ClientNameIcon />,
    },
    mobile: {
      labelLeftIcon: <PhoneIcon />,
    },
    telephone: {
      labelLeftIcon: <PhoneIcon />,
    },
    address: {
      labelLeftIcon: <AddressIcon />,
      // PoC: Loqate-backed typeahead for the 5 rollout countries, plain text
      // (LOCATION_FIELDS' default) for everyone else. Conditional spread is
      // required here — an explicit `type: undefined` would still overwrite
      // the default 'text' type via LocationForm's field/config merge.
      ...(isLoqateCountry(selectedCountry) && {
        type: 'address-search',
      }),
    },
    city: {
      labelLeftIcon: <AddressIcon />,
      ...(CITY_FIELD_LABEL_BY_COUNTRY[selectedCountry] && {
        label: CITY_FIELD_LABEL_BY_COUNTRY[selectedCountry],
      }),
    },
    state: {
      labelLeftIcon: <AddressIcon />,
      options: states || [],
      disabled: !selectedCountry,
      // IT's list (country-region.json) is provinces, not regions (see
      // AddressSearchField.js's resolveStateCode) — label it accordingly so
      // the field doesn't say "State" over a province picklist. Same
      // "Province" terminology applies to ES/PH/VN per the requirements doc;
      // only India genuinely uses "State".
      ...(STATE_FIELD_LABEL_BY_COUNTRY[selectedCountry] && {
        label: STATE_FIELD_LABEL_BY_COUNTRY[selectedCountry],
      }),
    },
    country: {
      labelLeftIcon: <AddressIcon />,
      options: COUNTRY_OPTIONS,
      disabled: true,
      visible: isAddLocation,
    },
    postCode: {
      labelLeftIcon: <AddressIcon />,
      ...(POST_CODE_FIELD_LABEL_BY_COUNTRY[selectedCountry] && {
        label: POST_CODE_FIELD_LABEL_BY_COUNTRY[selectedCountry],
      }),
    },

    salesforceEmail: {
      labelLeftIcon: <EmailIcon />,
    },

    leadId: {
      visible: hasRoleAccess(roles, ROLE_GROUPS.LEAD_ID_ACCESS),
    },

    abn: {
      labelLeftIcon: <PhoneIcon />,
      visible: selectedCountry === 'AU',
    },

    nzbn: {
      labelLeftIcon: <PhoneIcon />,
      visible: selectedCountry === 'NZ',
    },

    taxId: {
      labelLeftIcon: <PhoneIcon />,
      visible: isInternationalTaxIdCountry(selectedCountry),
    },

    locationName: {
      labelLeftIcon: <LocationIcon />,
      // FULSAS-11828: visible only for child locations (New Account child card, New Location,
      // Edit Location on a child) — never for single-location accounts or the parent card.
      visible: isChildLocation,
    },

    billTo: {
      visible: canShowBillTo({roles, isParent}),

      options: [
        {
          value: 'parent',
          label: 'Parent Pay',
        },
        {
          value: 'self',
          label: 'Self',
        },
      ],
    },

    salesRepOrPartner: {
      options: getSalesRepOptions(roles),

      disabled: false,

      // PartnerOps has no "No"/"Sales Rep" choice, so picking Keap or Legacy Partner
      // is mandatory — flag it so the label shows the required asterisk.
      required: hasRoleAccess(roles, PARTNER_ONLY_ROLES),

      // Reseller's sales channel/rep attribution is always fixed server-side
      // (resolveSalesRepIdentity) regardless of any client selection — same as
      // InfluencerOps/KeapPartner — so the toggle is not shown for these roles.
      visible: !hasRoleAccess(roles, [
        'InfluencerOps',
        'KeapPartner',
        'Reseller',
      ]),
    },

    overrideSST: {
      options: OVERRIDE_SST_OPTIONS,

      // Restricted to the PremisePilot role only — hidden for every other role across
      // all flows (Edit, Add Location, New Account), which all build field visibility
      // through this config.
      visible: hasRoleAccess(roles, ['PremisePilot']),
    },

    salesRepInfo: {
      visible: values.salesRepOrPartner === 'sales-rep',
      labelLeftIcon: <ClientNameIcon />,
    },

    xpCode: {
      visible: PARTNER_VALUES.includes(values.salesRepOrPartner),
      label: hasRoleAccess(roles, ['PartnerOps', 'DirectSales'])
        ? 'Please enter XP or Affiliate Code'
        : 'Please enter XP Code',
      labelLeftIcon: <ClientNameIcon />,
    },

    overrideRepInfo: {
      visible: values.overrideSST === 'yes',
      labelLeftIcon: <ClientNameIcon />,
    },

    // FULSAS-11921: visible only on the parent form when the DB feature flag is active.
    internalAccount: {
      visible: isParent && enableInternalAccountCheckbox,
    },
  }
}
